import type LayerViewModel from "@esWebAPI/es/map/LayerViewModel";
import type { Criteria, TerritoryLevelCriteria } from "@esWebAPI/es/model/Criteria";

import AbstractFilterControl from "./AbstractFilterControl";

export default class TerritoryLevelFilterControl extends AbstractFilterControl {
    level: number;
    parent: number;
    levels: {value: number, text: string}[];
    parents: {value: number, text: string}[];

    constructor(layerViewModel: LayerViewModel, level: number, parent?: number) {
        super(layerViewModel, "TerritoryLevel", "Territory View");

        this.level = level;
        this.parent = parent || -1;
        this.levels = [];
        this.parents = [];
    }

    init(): JQueryDeferred<AbstractFilterControl> {
        return es.api.territory.getTerritoryMeta(this.layerViewModel.getTerritoryMetaId()).then((territoryMeta) => {
            this.levels = territoryMeta.levelLabels.map((label, index) => {
                return {
                    'value': index + 1,
                    'text': label
                };
            });

            return this.updateParents();
        }).then(() => this);
    }

    destroy(silent?: boolean) {
        //this.aggregateValue = this.analysis = null;

        super.destroy(silent);
    }

    toCriteria(): TerritoryLevelCriteria {
        return {
            $type: "TerritoryLevel",
            level: this.level,
            parent: (this.parent && this.parent !== -1) ? this.parent : null
        };
    }

    canRemove() {
        return false;
    }

    fromCriteria(criteria: Criteria | Criteria[]): TerritoryLevelFilterControl {
        if (this.isMultipleCriteria(criteria)) {
            criteria = this.mergeCriteria(criteria);
        }

        this.level = criteria.level || -1;
        this.parent = criteria.parent || -1;

        return this;
    }

    setParent(parent: number | string) {
        this.parent = parseInt(parent);

        this.eventChannel.fireEvent("onUpdate");
    }

    setLevel(level: number | string): JQueryDeferred<void> {
        this.level = parseInt(level);
        this.parent = -1;

        return this.updateParents().then(() => {
            this.eventChannel.fireEvent("onUpdate");
        });
    }

    updateParents(): JQueryDeferred<void> {
        const text = this.level === 1 ? "BASE" : "Showing_All";
        this.parents = [{value: -1, text: $i18n(text)}];

        if (this.level !== 1) {
            var parentIdentifier = this.layerViewModel.getAttributeNames().includes("NAME") ? "NAME" : this.layerViewModel.getStyleSettingsObjRef().labelSettings.labelAttrs()[0];

            return es.api.feature.render({
                'layers': [{
                    'id': this.layerViewModel.getId(),
                    'criteria': {
                        '$type': 'Equal',
                        'attr': 'ES_LEVEL',
                        'value': this.level - 1
                    },
                    'sortOrder': {'attr': parentIdentifier}
                }]
            }, false, {
                'noCache': true // TOR9-7298 - Somehow we were being return the initial page load cached version (0 results when filter locked), need to disable cache to force to get the up-to-date list of features
            }).then((rs) => {
                rs.layers[0].features.forEach((f) => {
                    this.parents.push({value: f.id, text: f.attrs[parentIdentifier]});
                });
            });
        }

        return es.util.deferred.resolved();
    }

    private isMultipleCriteria(criteria: Criteria | Criteria[]): boolean {
        return Array.isArray(criteria)
            && criteria.length > 0
            && criteria.every((crit) => {
                return crit.level === criteria[0].level && crit.parent === criteria[0].parent;
            });
    }

    private mergeCriteria(criteria: Criteria[]): Criteria {
        return criteria[0];
    }

    static supportAttribute(): boolean {
        return false; //TerritoryLevelFilter always come from criteria
    }

    static supportCriteria(criteria: Criteria): boolean {
        return criteria.$type === "TerritoryLevel";
    }

    static fromCriteria(layerViewModel: LayerViewModel, criteria: Criteria): JQueryDeferred<TerritoryLevelFilterControl> {
        return new TerritoryLevelFilterControl(layerViewModel, -1).fromCriteria(criteria).init().then((filter: TerritoryLevelFilterControl) => {
            return filter.fromCriteria(criteria);
        });
    }

    // es.control.TerritoryLevelFilter = TerritoryLevelFilter;
}
